import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProfessionPicker } from "@/components/ProfessionPicker";
import { toast } from "sonner";

const ROLES = [
  { v: "candidate", label: "I'm looking for work" },
  { v: "hr", label: "I'm a recruiter / HR" },
  { v: "owner", label: "I'm a company owner" },
];

const SECTORS = [
  "IT & Software", "Journalism", "Construction", "Sales", "Hospitality & Cooking",
  "Household Help", "Healthcare", "Design", "Education", "Logistics", "Finance", "Retail", "Other",
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: params.get("role") || "candidate",
    sector: "", location: "", professions: [],
  });
  const [loading, setLoading] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      toast.success("Account created");
      navigate(u.role === "owner" ? "/owner" : "/feed");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-6 py-12">
      <h1 className="text-3xl font-bold tracking-tight">Create your account</h1>
      <p className="text-sm text-[#68706B] mt-2">Already in? <Link to="/login" className="text-[#1E3F33] font-semibold underline">Sign in</Link></p>

      <form onSubmit={submit} className="mt-8 space-y-3" data-testid="register-form">
        <Input placeholder="Full name" value={form.name} onChange={set("name")} className="rounded-none h-11" required data-testid="reg-name" />
        <Input type="email" placeholder="Email" value={form.email} onChange={set("email")} className="rounded-none h-11" required data-testid="reg-email" />
        <Input type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} className="rounded-none h-11" required minLength={6} data-testid="reg-password" />

        <Select value={form.role} onValueChange={set("role")}>
          <SelectTrigger className="rounded-none h-11" data-testid="reg-role"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
          </SelectContent>
        </Select>

        {form.role === "candidate" && (
          <>
            <Select value={form.sector} onValueChange={set("sector")}>
              <SelectTrigger className="rounded-none h-11" data-testid="reg-sector"><SelectValue placeholder="Your sector" /></SelectTrigger>
              <SelectContent>
                {SECTORS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Location (e.g., Bengaluru)" value={form.location} onChange={set("location")} className="rounded-none h-11" data-testid="reg-location" />
            <div className="pt-1">
              <div className="text-xs uppercase tracking-[0.2em] text-[#68706B] font-semibold mb-2">Your professions</div>
              <ProfessionPicker value={form.professions} onChange={(p) => setForm((f) => ({ ...f, professions: p }))} />
            </div>
          </>
        )}

        <Button type="submit" disabled={loading} className="w-full h-11 rounded-none bg-[#1E3F33] hover:bg-[#152C24]" data-testid="reg-submit">
          {loading ? "Creating..." : "Create account"}
        </Button>
      </form>
    </div>
  );
}
